import { UserRole } from "../../../modules/system"
import { PolicyAction } from "../../../services/iam"
import { TableName, TablesData } from "../../../tables"
import { dedupe, sorted } from "../../../utils/listUtils"
import { define } from "../../../utils/typeUtils"
import { RoutePath } from "../../routes"
import { Action, Permission, TableActions } from "../types"

export const policyName = (companyCode: string, role: UserRole) => `${companyCode}.${role.toLowerCase()}`
export const groupName = (companyCode: string, role: UserRole) => `${companyCode}.${role.toLowerCase()}.group`
export const roleName = (companyCode: string, role: UserRole) => `${companyCode}.${role.toLowerCase()}.role`

export const toIamPermissions = (permissions: Permission[]) =>
    permissions.map(({ tableNames, isIndex, hasTKey, actions }) => ({
        tables: tableNames,
        index: isIndex ? '*' : undefined,
        actions: dedupe(actions.flatMap(action => toPolicyActions(action, hasTKey)))
    }))

export const toPolicyActions = (action: Action, hasTKey: boolean): PolicyAction[] => {
    switch(action) {
        case Action.GET_ALL:
            return [hasTKey ? PolicyAction.QUERY : PolicyAction.SCAN]
        case Action.GET:
            return [PolicyAction.GET_ITEM]
        case Action.GET_BY_PARAMS:
            return [PolicyAction.SCAN]
        case Action.GET_BY_IDS:
            return [PolicyAction.BATCH_GET]
        case Action.SAVE:
            // Transactional writes (TransactWriteItems) are authorized by the single item actions they contain
            return [PolicyAction.BATCH_WRITE, PolicyAction.PUT_ITEM, PolicyAction.DELETE_ITEM, PolicyAction.UPDATE_ITEM]
        default:
            return [PolicyAction.QUERY]
    }
}

export const toPermissionList = (permissions: TableActions[]): Permission[] => {
    const tablePermissions = permissions.filter(permission => !permission.indexName)
    const indexPermissions = permissions.filter(permission => permission.indexName)
    
    const tableActionsMap = new Map<TableName, Action[]>()
    const tenantTableActionsMap = new Map<TableName, Action[]>()
    tablePermissions.forEach(permission => {
        const { tableName, actions } = permission
        const hasTKey = TablesData.get(tableName)?.hasTKey || false
        const setAction = (map: Map<TableName, Action[]>) => {
            if (map.has(tableName)) {
                const mapActions = define(map.get(tableName))
                actions.forEach(action => !mapActions.includes(action) && mapActions.push(action))
            } else {
                map.set(tableName, [...actions])
            }
        }
        setAction(hasTKey ? tenantTableActionsMap : tableActionsMap)
    })

    const actionsTablesMap = new Map<string, TableName[]>()
    const tenantActionsTablesMap = new Map<string, TableName[]>()
    const setTables = (sourceMap: Map<TableName, Action[]>, map: Map<string, TableName[]>) => {
        sourceMap.forEach((actions, tableName) => {
            const actionsKey = sorted(actions).join(':')
            if (map.has(actionsKey)) {
                const tableNames = define(map.get(actionsKey))
                tableNames.push(tableName)
            } else {
                map.set(actionsKey, [tableName])
            }
        })
    }
    setTables(tableActionsMap, actionsTablesMap)
    setTables(tenantTableActionsMap, tenantActionsTablesMap)
    
    const permissionList: Permission[] = []
    const setPermission = (map: Map<string, TableName[]>, hasTKey: boolean) => {
        map.forEach((tableNames, actionsKey) => {
            const actions = actionsKey.split(':') as Action[]
            permissionList.push({ tableNames, hasTKey, actions })
        })
    }
    setPermission(actionsTablesMap, false)
    setPermission(tenantActionsTablesMap, true)

    const indexPermissionsMap = new Map<string, string[]>()
    indexPermissions.forEach(permission => {
        const actionsKey = sorted(permission.actions).join(':')
        const tableIndexKey = `${permission.tableName}:${permission.indexName}`
        if (indexPermissionsMap.has(actionsKey)) {
            const tableIndexes = indexPermissionsMap.get(actionsKey)!
            if (!tableIndexes.includes(tableIndexKey)) {
                tableIndexes.push(tableIndexKey)
            }
        } else {
            indexPermissionsMap.set(actionsKey, [tableIndexKey])
        }
    })

    indexPermissionsMap.forEach((tableIndexKeys, actionsKey) => {
        const tableNames: TableName[] = []
        const indexNames: string[] = []
        tableIndexKeys.forEach(tableIndexKey => {
            const [tableName, indexName] = tableIndexKey.split(':')
            tableNames.push(tableName as TableName)
            indexNames.push(indexName)
        })
        const actions = actionsKey.split(':') as Action[]
        permissionList.push({
            tableNames,
            isIndex: true,
            hasTKey: true,
            actions
        })
    })

    return permissionList
}

export const getInitialRoute = (role: UserRole): RoutePath => {
    const initialPanels = new Map<UserRole, RoutePath>()
        .set(UserRole.MANAGER, RoutePath.INICIO)
        .set(UserRole.ADMINISTRATION, RoutePath.INICIO)
        .set(UserRole.PURCHASES, RoutePath.INICIO)
        .set(UserRole.SALES, RoutePath.INICIO)
        .set(UserRole.WAREHOUSE, RoutePath.INICIO)
        .set(UserRole.LOGISTICS, RoutePath.INICIO)

    return initialPanels.get(role) || RoutePath.COMPANY
}
